uniform vec3 uColorA;      // 明るい側の色
uniform vec3 uColorB;      // 陰側の色
uniform vec3 uRimColor;    // フレネルリムの色（ハローと揃える）
uniform vec3 uLightDir;    // 疑似太陽の方向（ワールド空間・固定）
uniform float uTime;
uniform float uAlpha;      // イントロのフェードイン用
uniform float uBandFreq;   // 縞の本数 / 模様のディテール周波数（スタイルごとに解釈）
uniform float uSeed;       // 天体ごとの模様の個体差

varying vec3 vWorldNormal;
varying vec3 vViewDir;
varying vec2 vUv;
varying vec3 vLocalPos;

#include ./noise.glsl

/*
 * 天体の表面。SURFACE_STYLE（ShaderMaterial の defines で注入）で模様が変わる:
 *   0: bands     — 従来の sin 縞（未指定惑星のデフォルト）
 *   1: saturn    — 細かい緯度縞（帯ごとの明暗 + 乱流うねり）
 *   2: sun       — fbm 粒状斑 + ホットスポット（ゆっくり沸き立つ）
 *   3: pearl     — 真珠質の氷。大理石状に流れる淡い光筋
 *   4: auroraIce — 氷ベース + 極域でゆらめくオーロラ光
 * half-lambert 陰影・フレネルリム・uAlpha フェードは全スタイル共通（家族感の維持）。
 */
void main()
{
    vec3 normal = normalize(vWorldNormal);
    vec3 dir = normalize(vLocalPos); // 球面方向（UV シーム / 極の歪みなし）

    // 実ライトは使わず half-lambert の疑似陰影で自己発光させる
    // （Environment の消灯演出中でも見え、暗いイントロを壊さない）
    float lambert = 0.5 + 0.5 * dot(normal, normalize(uLightDir));
    lambert = lambert * lambert;

    vec3 shaded = mix(uColorB, uColorA, lambert);
    vec3 base;

#if SURFACE_STYLE == 1
    // saturn: 細かい緯度縞。floor で帯を刻み、帯ごとの明るさ差とノイズのうねりを付ける
    float wobble = (noise3(dir * 4.0 + uSeed * 17.0) - 0.5) * 0.15;
    float y = (dir.y + wobble) * uBandFreq;
    float bandShade = 0.85 + 0.3 * hash1(floor(y) + uSeed * 61.0);
    float edge = smoothstep(0.0, 0.3, abs(fract(y) - 0.5)); // 帯境界を柔らかく暗く
    float band = bandShade * mix(1.0, 0.82, edge);
    band *= 0.94 + 0.06 * sin(dir.y * 5.0 + uSeed * 6.2831); // 低周波の大うねり
    base = shaded * band;

#elif SURFACE_STYLE == 2
    // sun: fbm 粒状斑 + ホットスポット（ドメインワープでゆっくり沸き立つ）
    vec3 p = dir * 5.0 + uSeed * 23.0;
    vec3 drift = vec3(0.0, uTime * 0.03, uTime * 0.02);
    float warp = fbm(p * 0.5 + drift);
    float granule = fbm(p + warp * 1.8 + drift * 1.5);
    float hot = smoothstep(0.55, 0.8, fbm(p * 0.45 - drift) + warp * 0.3);
    float heat = clamp(granule * 0.9 + hot * 0.6, 0.0, 1.0);
    base = mix(uColorB, uColorA, heat);            // 深橙→明黄のランプ
    base += uColorA * hot * 0.5;                   // ホットスポット増光
    base *= 0.75 + 0.45 * mix(1.0, lambert, 0.35); // 自己発光感を出すため陰影を弱める

#elif SURFACE_STYLE == 3
    // pearl: 真珠質の氷。大理石状にゆっくり流れる淡い光筋
    float flow = fbm(dir * 2.0 + uSeed * 31.0 + vec3(uTime * 0.04, uTime * 0.025, 0.0));
    float marble = 0.5 + 0.5 * sin(dot(dir, vec3(1.0, 0.6, 0.3)) * 3.0 + flow * 4.0);
    marble = smoothstep(0.2, 0.9, marble);
    base = shaded * (0.86 + 0.26 * marble); // 淡い光筋（強すぎると岩石っぽくなる）
    float sheen = pow(1.0 - max(dot(normal, normalize(vViewDir)), 0.0), 2.0);
    base += uColorA * sheen * 0.22;         // 真珠光沢（柔らかく広い照り）
    // 磨いた真珠の鏡面ハイライト（疑似太陽の反射光点 + まわりの柔らかい照り返し）
    vec3 halfDir = normalize(normalize(uLightDir) + normalize(vViewDir));
    float specDot = max(dot(normal, halfDir), 0.0);
    float spec = pow(specDot, 48.0) + 0.3 * pow(specDot, 8.0);
    base += uColorA * spec * 0.9;

#elif SURFACE_STYLE == 4
    // auroraIce: 氷ベース + 極域でゆらめくオーロラ光
    float ice = fbm(dir * 3.0 + uSeed * 41.0);
    base = shaded * (0.82 + 0.3 * ice);
    float polar = smoothstep(0.35, 0.8, abs(dir.y)); // 高緯度マスク
    float shimmer = fbm(vec3(dir.xz * 4.0, uTime * 0.25) + uSeed * 7.0);
    shimmer = smoothstep(0.35, 0.75, shimmer);
    float aurora = polar * shimmer * (0.7 + 0.3 * sin(uTime * 0.8 + dir.y * 10.0));
    base += uRimColor * aurora * 0.8; // haloColor のミント発光

#else
    // bands: 従来の緯度バンド模様（経度方向に軽くうねらせて縞をずらす）
    float band = sin(vUv.y * uBandFreq * 3.1415 + uSeed * 6.2831 + sin(vUv.x * 6.2831 + uSeed) * 0.6);
    band = band * 0.5 + 0.5;
    base = shaded * (0.85 + 0.3 * band);
#endif

    // フレネルリム（大気っぽい縁の光）
    float fresnel = pow(1.0 - max(dot(normal, normalize(vViewDir)), 0.0), 3.0);
    base += uRimColor * fresnel * 0.6;

    gl_FragColor = vec4(base, uAlpha);
}
